import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Button, Modal, Form, ProgressBar } from "react-bootstrap";
import { X, Trash, VolumeUpFill, ArrowRight } from "react-bootstrap-icons";
import { clearActiveExercise } from "../../../redux/slices/exerciseSlice";
import handlePronunciation from "../../../utils/handlePronunciation";
import {
  hideUserVocabularyWord,
  updateWordProgress,
} from "../../../redux/slices/userVocabularySlice";
import { updateUserItem } from "../../../utils/updateTableItem";

export default function MultipleChoice(props) {
  const dispatch = useDispatch(); // Initialize the Redux dispatch function
  const [currentIndex, setCurrentIndex] = useState(0); // State to track the current word index
  const currentWord = props.exerciseWords[currentIndex]; // Get the current word for the exercise
  const [selectedTranslation, setSelectedTranslation] = useState(null); // State to track the selected translation
  const [correctTranslation, setCorrectTranslation] = useState(null); // State to track the correct translation when wrong answer is selected
  const [currentTranslations, setCurrentTranslations] = useState([]); // State to store shuffled translations for the current word
  const [currentWordProgress, setCurrentWordProgress] = useState(0);
  const [showModal, setShowModal] = useState(false); // State to control the visibility of the modal
  const [isChecked, setIsChecked] = useState(false); // State to control checkbox
  const [progressUpdates, setProgressUpdates] = useState([]); // State to store progress updates

  useEffect(() => {
    setCurrentWordProgress(currentWord.progress); // Update progress whenever currentWord changes

    function generateTranslations(correct, falseOptions) {
      // Ensure only 4 false translations are selected
      const selectedFalseTranslations = falseOptions.slice(0, 4);
      const allTranslations = [correct, ...selectedFalseTranslations];
      return allTranslations.sort(() => Math.random() - 0.5);
    }
    let translations;

    if (props.reverse) {
      translations = generateTranslations(
        currentWord.word,
        props.falseTranslations.filter((word) => word !== currentWord.word) // Filter out the current word
      ); // Generate translations for the current word
    } else {
      translations = generateTranslations(
        currentWord.translation,
        props.falseTranslations.filter(
          (word) => word !== currentWord.translation
        ) // Filter out the current translation
      ); // Generate translations for the current word
    }

    setCurrentTranslations(translations); // Set the shuffled translations in state
  }, [
    currentIndex,
    currentWord.word,
    currentWord.progress,
    currentWord.translation,
    props.exerciseWords,
    props.falseTranslations,
    props.reverse,
  ]); // Re-run effect when currentIndex, exerciseWords, or falseTranslations change

  const updateProgress = useCallback(
    async (change) => {
      const newProgress = Math.max(
        1,
        Math.min(100, currentWordProgress + change)
      );

      setCurrentWordProgress(newProgress);

      setProgressUpdates((prevUpdates) => [
        ...prevUpdates,
        { id: currentWord.id, newProgress },
      ]);
    },
    [currentWordProgress, currentWord.id]
  );

  // Function to handle the selection of a translation
  const handleAnswerSelection = useCallback(
    (selectedTranslation, isDontKnow = false) => {
      setSelectedTranslation(selectedTranslation);

      if (props.reverse) {
        if (selectedTranslation !== currentWord.word || isDontKnow) {
          updateProgress(-5);
          setCorrectTranslation(currentWord.word); // Mark the correct translation if the selected one is wrong or if "Don't know" was clicked
        } else {
          updateProgress(10);
          setCorrectTranslation(null); // Clear correct translation marker if the answer is right
        }

        if (isChecked) {
          handlePronunciation(currentWord.word); // Pronounces current word on user's answer
        }
      } else {
        if (selectedTranslation !== currentWord.translation || isDontKnow) {
          updateProgress(-5);
          setCorrectTranslation(currentWord.translation); // Mark the correct translation if the selected one is wrong or if "Don't know" was clicked
        } else {
          updateProgress(10);
          setCorrectTranslation(null); // Clear correct translation marker if the answer is right
        }

        if (isChecked) {
          handlePronunciation(currentWord.word); // Pronounces current word on user's answer
        }
      }
    },
    [
      currentWord.word,
      currentWord.translation,
      props.reverse,
      isChecked,
      updateProgress,
    ]
  );

  // Wrap commitAllProgressUpdates in useCallback
  const commitAllProgressUpdates = useCallback(async () => {
    try {
      for (const update of progressUpdates) {
        await updateUserItem(update.id, null, update.newProgress);
      }

      // Dispatch all progress updates to the Redux store
      progressUpdates.forEach((update) => dispatch(updateWordProgress(update)));

      // Clear progress updates after committing
      setProgressUpdates([]);
    } catch (error) {
      console.error("Failed to update progress in DynamoDB", error);
    }
  }, [dispatch, progressUpdates]);

  // Function to move to the next word or finish the exercise
  const handleNext = useCallback(() => {
    if (currentIndex < props.exerciseWords.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1); // Move to the next word
      setCurrentWordProgress(currentWord.progress);
    } else {
      commitAllProgressUpdates(); // Commit all progress updates when exercise is finished
      setShowModal(true); // Show the modal when the exercise is complete
    }
    setSelectedTranslation(null); // Reset the selected translation
    setCorrectTranslation(null); // Reset correct translation
  }, [
    currentIndex,
    props.exerciseWords.length,
    commitAllProgressUpdates,
    currentWord.progress,
  ]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      const key = event.key;

      if (key >= "1" && key <= "5") {
        const index = parseInt(key, 10) - 1;
        if (
          index < currentTranslations.length &&
          selectedTranslation === null
        ) {
          handleAnswerSelection(currentTranslations[index]);
        }
      } else if (key === "Enter") {
        if (selectedTranslation === null) {
          handleAnswerSelection(currentWord.translation, true);
        } else {
          handleNext(); // Trigger the next word when Enter is pressed after a selection
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    currentTranslations,
    selectedTranslation,
    currentWord,
    handleAnswerSelection,
    handleNext,
  ]);

  // Function to reset the exercise and start again
  function handleTrainAgain() {
    props.onTrainAgain();
    setCurrentIndex(0); // Reset to the first word
    setShowModal(false); // Hide the modal
  }

  function handleCheckboxChange() {
    setIsChecked(!isChecked); // Toggle the checkbox state
  }

  // Handle the deletion of a word from the user's vocabulary
  function handleDeleteWord(wordId) {
    dispatch(hideUserVocabularyWord({ id: wordId }));
  }

  return (
    <div className="container mt-5">
      <style>
        {`
          @media (max-height: 840px) {
            .hide-on-small-height {
              display: none;
            }
          }
        `}
      </style>
      <div className="row justify-content-center">
        <div className="col-md-8 position-relative">
          <div
            className="card shadow-sm"
            style={{
              maxHeight: "80vh", // Ensure the card height fits within the screen
              overflowY: "auto", // Allow scrolling if content overflows
            }}
          >
            <div className="card-header d-flex justify-content-between align-items-center position-relative">
              <div>
                <h5 className="card-title mb-0">
                  {props.reverse ? "Transaltion - Word" : "Word - Translation"}
                </h5>
                <small className="text-muted">Vocabulary Training</small>
              </div>
              {/* Close button rendered at top-right corner on small screens */}
              <button
                className="btn btn-sm d-md-none" // d-md-none hides button on medium and larger screens
                style={{
                  border: "none",
                  background: "none",
                  padding: "0.5rem 1rem",
                  marginBottom: "5px",
                  borderRadius: "0.375rem",
                }}
                onClick={() => dispatch(clearActiveExercise())}
              >
                <X width="28" height="28" />
              </button>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 d-flex flex-column align-items-left">
                  <div className="d-flex align-items-center mb-3">
                    <h2 className="mb-1">
                      {props.reverse
                        ? currentWord.translation
                        : currentWord.word}
                    </h2>
                    {selectedTranslation && (
                      <Button
                        className="border-0 text-decoration-none ms-2"
                        variant="outline-secondary"
                        onClick={() => handlePronunciation(currentWord.word)}
                        style={{
                          backgroundColor: "transparent",
                          boxShadow: "none",
                          padding: "8px", // Adjust padding as needed
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "inherit",
                        }}
                      >
                        <VolumeUpFill width="24" height="24" />
                      </Button>
                    )}
                  </div>
                  <img
                    src={props.wordsSignedUrls[currentWord.id]}
                    alt="word"
                    className="img-fluid rounded hide-on-small-height"
                    style={{
                      width: "175px",
                      height: "175px",
                      objectFit: "cover",
                    }}
                  />
                  <div className="mt-auto pb-2 p-md-0">
                    {/* Bootstrap ProgressBar to display progress */}
                    <ProgressBar
                      now={currentWordProgress}
                      label={"Learning Progress"}
                      variant={currentWordProgress === 100 ? "success" : "info"}
                      style={{
                        height: "20px",
                      }}
                      className={
                        currentWordProgress === 100 ? "bg-transparent" : ""
                      }
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex flex-column">
                    {currentTranslations.map((translation, index) => (
                      <button
                        key={index}
                        className={`btn text-start mb-2 ${
                          props.reverse
                            ? selectedTranslation === translation
                              ? translation === currentWord.word
                                ? "btn-success"
                                : "btn-danger"
                              : translation === correctTranslation
                              ? "btn-success"
                              : "btn-outline-secondary"
                            : selectedTranslation === translation
                            ? translation === currentWord.translation
                              ? "btn-success"
                              : "btn-danger"
                            : translation === correctTranslation
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`} // Determine the button style based on the selected and correct translations
                        onClick={() => handleAnswerSelection(translation)}
                        disabled={selectedTranslation !== null} // Disable buttons after a selection is made
                      >
                        <span className="me-2 text-muted d-none d-md-inline">
                          {index + 1}
                        </span>{" "}
                        {translation}
                      </button>
                    ))}
                    {selectedTranslation ? (
                      <Button variant="primary" onClick={handleNext}>
                        Next <ArrowRight />
                      </Button>
                    ) : (
                      <button
                        className="btn btn-outline-secondary text-start"
                        onClick={() =>
                          handleAnswerSelection(currentWord.translation, true)
                        }
                      >
                        <span className="me-2 text-muted d-none d-md-inline">
                          Enter
                        </span>{" "}
                        Don't know 🙁
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer d-flex justify-content-between align-items-center">
              <Button
                className="btn btn-link text-muted"
                onClick={() => handleDeleteWord(currentWord.id)}
              >
                <Trash />
              </Button>
              <div className="d-flex">
                <Form className="me-3">
                  <Form.Check
                    type="checkbox"
                    id="autopronunciation-checkbox"
                    label="Autopronunciation"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                  />
                </Form>
                <span className="text-muted">
                  {currentIndex + 1}/{props.exerciseWords.length}{" "}
                  {/* Display current progress */}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-1 position-relative d-none d-md-block">
          {/* Button to exit the exercise (only shown on medium and larger screens) */}
          <button
            className="btn btn-sm position-absolute"
            style={{
              border: "none",
              background: "none",
              padding: "0.5rem 1rem",
              marginBottom: "5px",
              borderRadius: "0.375rem",
            }}
            onClick={() => dispatch(clearActiveExercise())}
          >
            <X width="28" height="28" />
          </button>
        </div>
      </div>

      {/* Modal to show when the exercise is complete */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Exercise Complete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Congratulations! You've completed the exercise.</p>
          <p>What would you like to do next?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => dispatch(clearActiveExercise())}
          >
            Back to exercises
          </Button>
          <Button variant="primary" onClick={handleTrainAgain}>
            Train again
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
